import React, { useState, useEffect } from 'react';
import axios from 'axios';

// AI Email Generation Settings Component (Simplified)
const AISettings = () => {
  // No state or logic needed here anymore as settings are centrally managed.

  return (
    <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
      {/* Informational message */}
      <div className="bg-gray-100 p-4 rounded-md border border-gray-200 mb-6">
         <p className="text-sm text-gray-700">
           AI Email Generation is configured and managed by the platform administrator.
           You do not need to provide your own API keys. Usage may be subject to billing based on your subscription plan.
         </p>
      </div>

      {/* Keep the "How to use" section */}
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <h5 className="text-sm font-medium text-gray-700 mb-2">How to use AI Email Generation</h5>
        <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
          {/* Step 1 is no longer relevant for the user */}
          {/* <li>Configure your chosen AI provider API key above</li> */}
          <li>Go to Email Marketing -- New Campaign</li>
          <li>Click the "Generate with AI" button next to the email content field</li>
          <li>Fill in the topic, audience, goal and language details</li>
          <li>Click "Generate Email" to create professional HTML email content</li>
          <li>Edit the generated content as needed before sending</li>
        </ol>
      </div>
    </div>
  );
};

const BulkEmailSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    region: '',
    accessKeyId: '',
    secretAccessKey: '',
    from_email: '',
    from_name: ''
  });

  // State for sender profiles
  const [senderProfiles, setSenderProfiles] = useState([]);
  const [newProfile, setNewProfile] = useState({ email: '', name: '', isDefault: false });
  const [editingProfile, setEditingProfile] = useState(null);
  const [showAddProfile, setShowAddProfile] = useState(false);

  // Load existing SES configuration
  useEffect(() => {
    const loadSesConfig = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');

        const config = {
          headers: {
            'x-auth-token': token
          }
        };

        console.log('Frontend: Loading SES config...');
        const response = await axios.get('/api/settings/aws-ses', config);
        console.log('Frontend: Received SES config:', response.data);

        if (response.data) {
          // Set the main form data
          const formDataToSet = {
            region: response.data.aws_ses_region || '',
            accessKeyId: response.data.aws_ses_access_key_id || '',
            secretAccessKey: response.data.aws_ses_secret_access_key || '',
            from_email: response.data.aws_ses_from_email || '',
            from_name: response.data.aws_ses_from_name || ''
          };
          console.log('Frontend: Setting form data to:', formDataToSet);
          setFormData(formDataToSet);

          // Set sender profiles if they exist
          try {
            if (response.data.aws_ses_sender_profiles) {
              // Parse the sender profiles which is stored as a JSON string in the database
              let profiles = response.data.aws_ses_sender_profiles;
              
              // If it's a string, try to parse it as JSON
              if (typeof profiles === 'string') {
                try {
                  profiles = JSON.parse(profiles);
                  console.log('Frontend: Parsed sender profiles:', profiles);
                } catch (jsonError) {
                  console.error('Frontend: Error parsing sender profiles JSON:', jsonError);
                  profiles = [];
                }
              }
              
              if (Array.isArray(profiles)) {
                setSenderProfiles(profiles);
              } else {
                console.warn('Frontend: Expected profiles to be an array, got:', typeof profiles);
                setSenderProfiles([]);
              }
            } else if (response.data.aws_ses_from_email) {
              // Create a default profile from the legacy from_email/from_name if no profiles exist
              setSenderProfiles([
                {
                  id: 'default_profile',
                  email: response.data.aws_ses_from_email,
                  name: response.data.aws_ses_from_name || '',
                  isDefault: true
                }
              ]);
            } else {
              console.log('Frontend: No sender profiles found, setting empty array');
              setSenderProfiles([]);
            }
          } catch (profilesError) {
            console.error('Frontend: Error processing sender profiles:', profilesError);
            setSenderProfiles([]);
          }
        }
      } catch (err) {
        setError('Error loading SES configuration');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadSesConfig();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle profile form input changes
  const handleProfileInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (editingProfile) {
      setEditingProfile({
        ...editingProfile,
        [name]: type === 'checkbox' ? checked : value
      });
    } else {
      setNewProfile({
        ...newProfile,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Handle adding a new profile
  const handleAddProfile = async () => {
    if (!newProfile.email) {
      setError('Email address is required for the sender profile');
      return;
    }

    // Generate a unique ID for the new profile
    const profileId = `profile_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

    // If this is set as default, remove default from other profiles
    let updatedProfiles = [...senderProfiles];
    if (newProfile.isDefault) {
      updatedProfiles = updatedProfiles.map(profile => ({
        ...profile,
        isDefault: false
      }));
    }

    // Add the new profile
    updatedProfiles.push({
      id: profileId,
      email: newProfile.email,
      name: newProfile.name || '',
      isDefault: newProfile.isDefault || updatedProfiles.length === 0 // Make first profile default
    });

    // Update state
    setSenderProfiles(updatedProfiles);
    setNewProfile({ email: '', name: '', isDefault: false });
    setShowAddProfile(false);

    // Automatically save the profiles to the server
    try {
      setIsSaving(true);

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Save the sender profiles
      const requestData = {
        senderProfiles: updatedProfiles,
        updateType: 'sender_profiles'
      };

      await axios.post('/api/settings/aws-ses', requestData, config);

      // Update the default sender info for backward compatibility
      const defaultProfile = updatedProfiles.find(p => p.isDefault) || updatedProfiles[0];
      if (defaultProfile) {
        setFormData(prev => ({
          ...prev,
          from_email: defaultProfile.email,
          from_name: defaultProfile.name || ''
        }));
      }

      setSuccess('Sender profile added and saved successfully.');
    } catch (err) {
      setError('Error saving profile: ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle editing a profile
  const handleSaveProfileEdit = async () => {
    if (!editingProfile || !editingProfile.email) {
      setError('Email address is required for the sender profile');
      return;
    }

    // If this is set as default, remove default from other profiles
    let updatedProfiles = [...senderProfiles];
    if (editingProfile.isDefault) {
      updatedProfiles = updatedProfiles.map(profile => ({
        ...profile,
        isDefault: profile.id === editingProfile.id ? true : false
      }));
    } else {
      // Make sure at least one profile is default
      const hasDefaultProfile = updatedProfiles.some(
        profile => profile.id !== editingProfile.id && profile.isDefault
      );

      if (!hasDefaultProfile) {
        setError('At least one profile must be set as default');
        return;
      }

      // Update the profile being edited
      updatedProfiles = updatedProfiles.map(profile =>
        profile.id === editingProfile.id ? editingProfile : profile
      );
    }

    // Update state
    setSenderProfiles(updatedProfiles);
    setEditingProfile(null);

    // Automatically save the updated profiles to the server
    try {
      setIsSaving(true);

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Save the sender profiles
      const requestData = {
        senderProfiles: updatedProfiles,
        updateType: 'sender_profiles'
      };

      await axios.post('/api/settings/aws-ses', requestData, config);

      // Update the default sender info for backward compatibility
      const defaultProfile = updatedProfiles.find(p => p.isDefault) || updatedProfiles[0];
      if (defaultProfile) {
        setFormData(prev => ({
          ...prev,
          from_email: defaultProfile.email,
          from_name: defaultProfile.name || ''
        }));
      }

      setSuccess('Sender profile updated and saved successfully.');
    } catch (err) {
      setError('Error saving profile: ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle deleting a profile
  const handleDeleteProfile = async (profileId) => {
    // Check if it's the default profile and there are others
    const profileToDelete = senderProfiles.find(p => p.id === profileId);

    if (profileToDelete.isDefault && senderProfiles.length > 1) {
      setError('Cannot delete the default profile. Set another profile as default first.');
      return;
    }

    // Remove the profile
    const updatedProfiles = senderProfiles.filter(profile => profile.id !== profileId);

    // If we deleted the only profile, clear the sender profiles
    if (updatedProfiles.length === 0) {
      setSenderProfiles([]);
    } else {
      // If we deleted the default profile, set the first one as default
      if (profileToDelete.isDefault) {
        updatedProfiles[0].isDefault = true;
      }

      setSenderProfiles(updatedProfiles);
    }

    // Automatically save the updated profiles to the server
    try {
      setIsSaving(true);

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Save the sender profiles
      const requestData = {
        senderProfiles: updatedProfiles,
        updateType: 'sender_profiles'
      };

      await axios.post('/api/settings/aws-ses', requestData, config);

      // Update the default sender info for backward compatibility
      if (updatedProfiles.length > 0) {
        const defaultProfile = updatedProfiles.find(p => p.isDefault) || updatedProfiles[0];
        setFormData(prev => ({
          ...prev,
          from_email: defaultProfile.email,
          from_name: defaultProfile.name || ''
        }));
      }

      setSuccess('Sender profile removed and changes saved successfully.');
    } catch (err) {
      setError('Error saving changes: ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to save all sender profiles
  const saveProfiles = async () => {
    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Save only the sender profiles
      const requestData = {
        senderProfiles: senderProfiles,
        updateType: 'sender_profiles'
      };

      await axios.post('/api/settings/aws-ses', requestData, config);

      // Also update the default sender info for backward compatibility
      const defaultProfile = senderProfiles.find(p => p.isDefault) || senderProfiles[0];
      if (defaultProfile) {
        setFormData(prev => ({
          ...prev,
          from_email: defaultProfile.email,
          from_name: defaultProfile.name || ''
        }));
      }

      setSuccess('Sender profiles saved successfully');
    } catch (err) {
      setError('Error saving sender profiles: ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Check if we're only updating sender information
      const isSenderInfoUpdate =
        formData.from_email !== undefined ||
        formData.from_name !== undefined;

      // If there's no region/accessKey in the form, it means we're just updating sender info
      const hasCredentials =
        formData.region &&
        formData.accessKeyId &&
        formData.secretAccessKey;

      // Prepare the data to send
      const requestData = {
        ...formData,
        // Include sender profiles in the full configuration
        senderProfiles: senderProfiles,
        // Specify the update type based on what's being updated
        updateType: isSenderInfoUpdate && !hasCredentials ? 'sender_info' : 'full'
      };

      const response = await axios.post('/api/settings/aws-ses', requestData, config);

      setSuccess('SES configuration saved successfully');
      setTestStatus(null);
    } catch (err) {
      setError('Error saving SES configuration: ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTestStatus('testing');
      setError('');

      const token = localStorage.getItem('token');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      const response = await axios.get('/api/settings/aws-ses/test', config);

      if (response.data && response.data.success) {
        setTestStatus('success');
        setSuccess(response.data.message || 'Connection successful');
      } else {
        setTestStatus('failed');
        setError(response.data && response.data.message || 'Connection test failed');
      }
    } catch (err) {
      setTestStatus('failed');
      setError('Error testing connection: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  if (isLoading) {
    return <p>Loading configuration...</p>;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium mb-4">Amazon SES Configuration</h3>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Amazon SES integration is used for the bulk email marketing tool. These settings are separate from your SMTP profiles used for individual emails.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label htmlFor="region" className="block text-sm font-medium text-gray-700">
              AWS Region
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="region"
                id="region"
                value={formData.region}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g. us-east-1"
                required
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="accessKeyId" className="block text-sm font-medium text-gray-700">
              Access Key ID
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="accessKeyId"
                id="accessKeyId"
                value={formData.accessKeyId}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                required
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="secretAccessKey" className="block text-sm font-medium text-gray-700">
              Secret Access Key
            </label>
            <div className="mt-1">
              <input
                type={formData.secretAccessKey.includes('•') ? 'text' : 'password'}
                name="secretAccessKey"
                id="secretAccessKey"
                value={formData.secretAccessKey}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder={formData.secretAccessKey.includes('•') ? 'Leave unchanged to keep current key' : ''}
                required={!formData.secretAccessKey.includes('•')}
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Note: Leave this field unchanged if you don't want to update the secret key.
            </p>
          </div>

          <div className="sm:col-span-6 border-t border-gray-200 pt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">Sender Settings</h4>
            <p className="text-sm text-gray-600 mb-4">
              Configure sender information for emails sent through Amazon SES. All email addresses must be verified in your SES account.
            </p>

            {/* Sender Profiles */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h5 className="text-sm font-medium text-gray-800">Sender Profiles</h5>
                <button
                  type="button"
                  onClick={() => setShowAddProfile(true)}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Add Profile
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Configure multiple sender profiles to use different email addresses and from names when sending.
                Each email must be verified in your SES account.
              </p>

              {/* Profiles List */}
              {senderProfiles.length > 0 ? (
                <div className="bg-white rounded-md border border-gray-200 overflow-hidden mb-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          From Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Default
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {senderProfiles.map((profile) => (
                        <tr key={profile.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {editingProfile && editingProfile.id === profile.id ? (
                              <input
                                type="email"
                                name="email"
                                value={editingProfile.email}
                                onChange={handleProfileInputChange}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                              />
                            ) : (
                              profile.email
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {editingProfile && editingProfile.id === profile.id ? (
                              <input
                                type="text"
                                name="name"
                                value={editingProfile.name}
                                onChange={handleProfileInputChange}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="From Name"
                              />
                            ) : (
                              profile.name || <span className="text-gray-400 italic">None</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {editingProfile && editingProfile.id === profile.id ? (
                              <input
                                type="checkbox"
                                name="isDefault"
                                checked={editingProfile.isDefault}
                                onChange={handleProfileInputChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            ) : (
                              profile.isDefault && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Default
                                </span>
                              )
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {editingProfile && editingProfile.id === profile.id ? (
                              <div className="flex justify-end space-x-2">
                                <button
                                  type="button"
                                  onClick={handleSaveProfileEdit}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingProfile(null)}
                                  className="text-gray-600 hover:text-gray-900"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end space-x-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingProfile({...profile})}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteProfile(profile.id)}
                                  disabled={profile.isDefault && senderProfiles.length > 1}
                                  className={`${
                                    profile.isDefault && senderProfiles.length > 1
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-red-600 hover:text-red-900'
                                  }`}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4 text-center">
                  <p className="text-sm text-gray-600">
                    No sender profiles configured yet. Click "Add Profile" to create one.
                  </p>
                </div>
              )}

              {/* Add/Edit Profile Form */}
              {showAddProfile && (
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4">
                  <h6 className="text-sm font-medium text-gray-800 mb-3">Add New Sender Profile</h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label htmlFor="profile_email" className="block text-sm font-medium text-gray-700">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="profile_email"
                        name="email"
                        value={newProfile.email}
                        onChange={handleProfileInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="verified@yourdomain.com"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="profile_name" className="block text-sm font-medium text-gray-700">
                        From Name
                      </label>
                      <input
                        type="text"
                        id="profile_name"
                        name="name"
                        value={newProfile.name}
                        onChange={handleProfileInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="e.g. John Smith"
                      />
                    </div>
                  </div>
                  <div className="flex items-center mb-3">
                    <input
                      id="profile_default"
                      name="isDefault"
                      type="checkbox"
                      checked={newProfile.isDefault}
                      onChange={handleProfileInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="profile_default" className="ml-2 block text-sm text-gray-700">
                      Set as default sender
                    </label>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowAddProfile(false)}
                      className="py-1 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddProfile}
                      className="py-1 px-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Add Profile
                    </button>
                  </div>
                </div>
              )}

            </div>

          </div>

          <div className="sm:col-span-6">
            <div className="flex justify-between">
              <button
                type="button"
                onClick={testConnection}
                disabled={isSaving || testStatus === 'testing'}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${
                  testStatus === 'testing'
                    ? 'text-gray-500 bg-gray-100'
                    : testStatus === 'success'
                    ? 'text-green-700 bg-green-50 border-green-300'
                    : testStatus === 'failed'
                    ? 'text-red-700 bg-red-50 border-red-300'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {testStatus === 'testing' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Testing Connection...
                  </>
                ) : testStatus === 'success' ? (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Connection Successful
                  </>
                ) : testStatus === 'failed' ? (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    Test Connection Again
                  </>
                ) : (
                  'Test Connection'
                )}
              </button>

              <div>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Configuration'
                  )}
                </button>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Make sure to verify your sender email address in the AWS SES console before sending emails.
            </p>
          </div>
        </div>
      </form>

      {/* AI Email Generation Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium mb-4">AI Email Generation</h3>

        <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-indigo-700">
                Configure AI email generation using the OpenAI API. This enables the "Generate with AI" feature in the email marketing tool.
              </p>
            </div>
          </div>
        </div>

        <AISettings />
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <h4 className="text-md font-medium mb-4">Bulk Email Marketing Tool</h4>
        <p className="text-sm text-gray-600 mb-4">
          Once you have configured and tested your AWS SES credentials, you can use the bulk email marketing tool to send campaigns.
        </p>
        <a
          href="/email-marketing"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Go to Email Marketing
        </a>
      </div>
    </div>
  );
};

export default BulkEmailSettings;
